import { useContext } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useAppEvent from 'hooks/useAppEvent';
import useAppModal from 'hooks/useAppModal';
import { AppEventType } from 'models/Events';
import { LeadStatus } from 'models/Leads';
import { openInboxForLead } from 'utils/inbox/inboxUtils';
import {
  canSendMessage,
  isLeadAMainBooking,
  isLeadASubBooking,
} from 'utils/lead/leadUtils';
import { normalizeKey } from 'utils/localeUtils';
import DollarIcon from 'assets/images/icons/dollar.svg?react';
import PencilIcon from 'assets/images/icons/pencil.svg?react';
import HomeIcon from 'assets/images/icons/home.svg?react';
import useFeatures from 'hooks/useFeatures';
import useAppUser from 'hooks/useAppUser';
import SelectField from 'components/forms/selectField/SelectField';
import LeadActionsPopoverWithDefaultActions from '../actions/LeadActionsPopoverWithDefaultActions';
import LeadChannelIcon from '../LeadChannelIcon';
import LeadCircle from '../status/LeadCircle';
import LeadMultiReservationLabel from '../LeadMultiReservationLabel';
import LeadModalContext from './LeadModalContext';
import {
  GroupBookingContainer,
  LeadModalChannelIconContainer,
  LeadModalHeaderButton,
  LeadModalHeaderButtonToolbar,
  LeadModalHeaderContainer,
  LeadModalHeaderSection,
  LeadModalHeaderStyled,
  RightSidedContainer,
  StatusButton,
  StatusButtonsContainer,
} from './LeadModalHeader.styles';
import LeadModalHeaderTitle from './LeadModalHeaderTitle';
import LeadModalHeaderTags from './LeadModalHeaderTags';
import { LeadModalParams } from './LeadModal.types';
import useLeadGroupBookingValues from './useLeadGroupBookingValues';
import useLeadRebook from './useLeadRebook';

const newLeadAvailableStatuses = [
  LeadStatus.NEW,
  LeadStatus.ON_HOLD,
  LeadStatus.BOOKED_BY_AGENT,
  LeadStatus.BLOCKED,
];

const LeadModalHeader = ({
  params: { jobCreationNotAllowed },
}: {
  params: LeadModalParams;
}) => {
  const { t } = useTranslation();
  const { publish } = useAppEvent();
  const { closeModal } = useAppModal();
  const { lead, modalId } = useContext(LeadModalContext);
  const { setValue } = useFormContext();
  const { allowOrderViewWebapp } = useFeatures();
  const { isEmployeeManager } = useAppUser();
  const { showGroupBookingSelect, groupBookingOptions } =
    useLeadGroupBookingValues();
  const { isRebookActionAvailable, prepareLeadRebookForm } = useLeadRebook();

  const {
    checkInDate,
    checkOutDate,
    propertyUid,
    status: currentStatus,
    uid: leadUid,
  } = useWatch();
  const updateMode = !!leadUid;

  const sendMessageHandler = () => {
    openInboxForLead(leadUid);
  };

  const statusButtonClickHandler = (status: LeadStatus) => {
    // TODO delete once modals merged
    if (status === LeadStatus.BLOCKED) {
      closeModal(modalId);
      publish(AppEventType.OPEN_CREATE_BLOCK_MODAL, {
        dateFrom: checkInDate,
        dateTo: checkOutDate,
        propertyUid,
      });
    } else {
      setValue('status', status);
    }
  };

  const onCreateJobClick = () => {
    closeModal(modalId);
    publish(AppEventType.OPEN_CREATE_JOB_MODAL, {
      jobDate: checkInDate,
      propertyUid,
    });
  };

  const isMainBooking = isLeadAMainBooking(lead!);
  const showRebookButton =
    isRebookActionAvailable && updateMode && !isMainBooking;

  return (
    <LeadModalHeaderStyled>
      <LeadModalHeaderContainer>
        <LeadModalHeaderSection>
          {updateMode && lead && (
            <>
              <LeadActionsPopoverWithDefaultActions
                baseId="lead-modal-actions-popover"
                containerSelector=".modal-header"
                lead={lead}
                placement="right"
              >
                <LeadCircle lead={lead} />
              </LeadActionsPopoverWithDefaultActions>
              <LeadModalChannelIconContainer>
                {/*
                 // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified */}
                <LeadChannelIcon channel={lead.channel?.channel} />
              </LeadModalChannelIconContainer>
            </>
          )}
          <LeadModalHeaderTitle />
          <LeadModalHeaderTags />
        </LeadModalHeaderSection>

        <RightSidedContainer>
          {showGroupBookingSelect && (
            <GroupBookingContainer>
              {/*
               // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified */}
              <LeadMultiReservationLabel lead={lead} />
              <SelectField
                name="currentGroupBookingLeadUid"
                // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
                options={groupBookingOptions}
              />
            </GroupBookingContainer>
          )}

          <LeadModalHeaderSection>
            <LeadModalHeaderButtonToolbar>
              {updateMode &&
                isEmployeeManager &&
                // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
                lead.order &&
                // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
                lead.order.uid &&
                // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
                !isLeadASubBooking(lead) && (
                  <LeadModalHeaderButton
                    href={
                      allowOrderViewWebapp
                        ? // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
                          `#/order/${lead.order.uid}`
                        : // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
                          `#/order-view?o=${lead.order.uid}`
                    }
                    data-testid="view-order-link"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <DollarIcon />
                    {t('componentOrder.viewOrder')}
                  </LeadModalHeaderButton>
                )}

              {!jobCreationNotAllowed &&
                // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
                !isLeadAMainBooking(lead) &&
                // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
                !isLeadASubBooking(lead) && (
                  <LeadModalHeaderButton
                    data-testid="create-job-button"
                    onClick={onCreateJobClick}
                  >
                    <HomeIcon />
                    {t('componentLead.modal.createJob')}
                  </LeadModalHeaderButton>
                )}

              {updateMode &&
                lead &&
                canSendMessage(lead) &&
                !isLeadASubBooking(lead) && (
                  <LeadModalHeaderButton
                    data-testid="send-message-button"
                    onClick={sendMessageHandler}
                  >
                    <PencilIcon />
                    {t('componentLead.modal.sendMessage')}
                  </LeadModalHeaderButton>
                )}

              {showRebookButton && (
                <LeadModalHeaderButton
                  data-testid="rebook-button"
                  onClick={prepareLeadRebookForm}
                >
                  {t('componentLead.modal.rebook')}
                </LeadModalHeaderButton>
              )}
            </LeadModalHeaderButtonToolbar>

            {!updateMode && (
              <StatusButtonsContainer>
                {newLeadAvailableStatuses.map((status) => (
                  <StatusButton
                    $fill={status === currentStatus}
                    $status={status}
                    key={status}
                    onClick={() => statusButtonClickHandler(status)}
                  >
                    {t(
                      normalizeKey(
                        `componentLead.modal.newLeadStatus.${status}`,
                      ),
                    )}
                  </StatusButton>
                ))}
              </StatusButtonsContainer>
            )}
          </LeadModalHeaderSection>
        </RightSidedContainer>
      </LeadModalHeaderContainer>
    </LeadModalHeaderStyled>
  );
};

export default LeadModalHeader;
